import { useEffect, useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CarouselHero from '@/components/base/CarouselHero';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { CHURCH_WORSHIP_PHOTOS } from '@/lib/churchPhotos';
import { supabase } from '@/lib/supabase';

export default function ContactPage() {
  const { data: settings } = useSiteSettings();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');
  const MAX_LENGTH = 500;

  const phones = settings?.phones ?? [];
  const emails = settings?.emails ?? [];
  const locations = settings?.locations ?? [];
  const social = settings?.social ?? { facebook: '#', twitter: '#', instagram: '#', youtube: '#' };

  useEffect(() => {
    document.title = 'Contact TGMCI — Get in Touch';
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }); },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (honeypot.trim()) { setFormStatus('success'); setFormMsg('Your message has been sent. We will get back to you soon!'); return; }
    if (message.length > MAX_LENGTH) return;
    if (!name || !email || !message) return;
    setFormStatus('loading');

    const { error: insertErr } = await supabase.from('contact_submissions').insert({
      name,
      email,
      message,
    });

    if (insertErr) {
      setFormStatus('error');
      setFormMsg('Something went wrong. Please try again.');
      return;
    }

    // Best-effort email notification to the church (non-blocking).
    const notifyTo = emails[0] || 'info@tgmci.org';
    supabase.functions
      .invoke('send-email', {
        body: {
          to: notifyTo,
          subject: `New message from ${name}`,
          text: `Name: ${name}\nEmail: ${email}\n\nMessage:\n${message}`,
          replyTo: email,
        },
      })
      .then(() => undefined)
      .catch(() => undefined);

    setFormStatus('success');
    setFormMsg('Your message has been sent! We will get back to you as soon as possible.');
    setName(''); setEmail(''); setMessage('');
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <CarouselHero
          title="Get in Touch with Our Church Family"
          subtitle="We would love to hear from you. Whether you have a question, need assistance, or want to connect."
          images={CHURCH_WORSHIP_PHOTOS}
          breadcrumb="Contact"
        />

        {/* Contact Section */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Left — Info */}
              <div className="reveal-left">
                <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">Reach Out</p>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6">We&apos;re Here for You</h2>
                <div className="w-12 h-1 bg-accent-500 mb-6" />
                <p className="text-foreground-600 text-base leading-relaxed mb-8">
                  We would love to hear from you. Whether you have a question, need assistance, or want to connect with our community, feel free to reach out to us using the contact details below or the form.
                </p>

                <div className="space-y-6 mb-8">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600 shrink-0">
                      <i className="ri-phone-line text-xl" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-foreground-800 mb-1">Phone Numbers</h3>
                      {phones.map((p) => (
                        <div key={p.number}>
                          <span className="text-foreground-400 text-xs uppercase">{p.label}: </span>
                          <a href={`tel:${p.number}`} className="text-foreground-600 hover:text-primary-600 transition-colors text-sm">
                            {p.number}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600 shrink-0">
                      <i className="ri-mail-line text-xl" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-foreground-800 mb-1">Email Addresses</h3>
                      {emails.map((em) => (
                        <a key={em} href={`mailto:${em}`} className="block text-foreground-600 hover:text-primary-600 transition-colors text-sm">
                          {em}
                        </a>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600 shrink-0">
                      <i className="ri-map-pin-line text-xl" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-foreground-800 mb-2">Our Locations</h3>
                      {locations.map((loc) => (
                        <div key={loc.label} className="mb-2">
                          <span className="text-xs font-label font-semibold text-accent-600 uppercase">{loc.label}: </span>
                          <span className="text-foreground-600 text-sm">{loc.address}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-heading font-semibold text-foreground-800 mb-4">Follow Us</h3>
                  <div className="flex gap-3">
                    {[
                      { icon: 'ri-facebook-fill', label: 'Facebook', href: social.facebook },
                      { icon: 'ri-twitter-x-fill', label: 'Twitter/X', href: social.twitter },
                      { icon: 'ri-instagram-line', label: 'Instagram', href: social.instagram },
                      { icon: 'ri-youtube-fill', label: 'YouTube', href: social.youtube },
                    ].map((s) => (
                      <a
                        key={s.icon}
                        href={s.href || '#'}
                        aria-label={s.label}
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600 hover:bg-primary-600 hover:text-white transition-colors cursor-pointer"
                      >
                        <i className={`${s.icon} text-base`} aria-hidden="true" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right — Form */}
              <div className="reveal">
                {formStatus === 'success' ? (
                  <div className="h-full flex flex-col items-center justify-center text-center p-10 bg-green-50 border border-green-200 rounded-2xl">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-600 mb-4">
                      <i className="ri-check-double-line text-3xl" aria-hidden="true" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-green-800 mb-2">Message Sent!</h3>
                    <p className="text-green-700 text-sm leading-relaxed">{formMsg}</p>
                    <button type="button" onClick={() => setFormStatus('idle')} className="btn-primary mt-6">
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form
                    id="contact-form"
                    onSubmit={handleSubmit}
                    className="bg-background-100 rounded-2xl border border-background-200 p-8 space-y-5"
                    noValidate
                  >
                    <h3 className="font-heading font-bold text-xl text-foreground-800">Send Us a Message</h3>

                    <div style={{ position: 'absolute', left: '-9999px' }}>
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
                    </div>

                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-label font-medium text-foreground-600 uppercase tracking-wide mb-1.5">
                        Full Name
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        placeholder="Your full name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        className="w-full px-4 py-3 border border-background-300 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-label font-medium text-foreground-600 uppercase tracking-wide mb-1.5">
                        Email Address
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        placeholder="Your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-4 py-3 border border-background-300 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-label font-medium text-foreground-600 uppercase tracking-wide mb-1.5">
                        Message
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={5}
                        placeholder="Your message..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value.slice(0, MAX_LENGTH))}
                        required
                        className="w-full px-4 py-3 border border-background-300 rounded-lg text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-colors resize-none"
                      />
                      <p className={`text-xs mt-1 text-right ${message.length >= MAX_LENGTH ? 'text-red-500' : 'text-foreground-400'}`}>
                        {message.length}/{MAX_LENGTH}
                      </p>
                    </div>

                    {formStatus === 'error' && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-600 text-sm">{formMsg}</p>
                        <button type="button" onClick={() => setFormStatus('idle')} className="text-xs text-red-500 underline mt-1 cursor-pointer">Try again</button>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'loading' || message.length > MAX_LENGTH}
                      className="btn-primary w-full justify-center py-4 text-base"
                    >
                      {formStatus === 'loading' ? (
                        <><i className="ri-loader-4-line animate-spin" aria-hidden="true" /> Sending...</>
                      ) : (
                        <><i className="ri-send-plane-line" aria-hidden="true" /> Send Message</>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="h-[450px] bg-background-200">
          <iframe
            src="https://maps.google.com/maps?q=Edea%2C+Cameroon&z=12&hl=en&t=m&output=embed"
            width="100%"
            height="100%"
            loading="lazy"
            title="TGMCI Edea, Cameroon location map"
            className="border-0"
            aria-label="Map showing TGMCI location in Edea, Cameroon"
          />
        </section>
      </main>
      <Footer />
    </div>
  );
}